'use client'

import { useEffect, useMemo, useState } from 'react'
import analyticsService from '../analytics/analytics.instance'
import { extractRegionFromUrl } from '../utils/helpers'
import {
  setPageVariantInClarity,
  setNavigationVariantInClarity,
  setFooterVariantInClarity,
} from '../utils/clarity-variant'
import { AbTestingConfiguration, SelectedModuleVariants } from '../services/sanity/types'
import { getVariantIdCookie } from '../utils/analytics'

const useClarityReady = () => {
  const [isClarityReady, setIsClarityReady] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    let attempts = 0
    const maxAttempts = 5

    const checkClarity = () => {
      if (window?.clarity && typeof window.clarity === 'function') {
        setIsClarityReady(true)
      } else if (attempts < maxAttempts) {
        attempts++
        setTimeout(checkClarity, 100)
      }
    }

    checkClarity()
  }, [])

  return isClarityReady
}

export type PageVariantConfig = {
  pageReference: { template: { id: string } }
  variantName: string
}

export type AnalyticsTrackerProps = {
  abTestingConfiguration?: AbTestingConfiguration
  moduleABTestingConfiguration?: SelectedModuleVariants
  pageType?: string
  id?: string
  title: string
}

export function AnalyticsTracker({
  abTestingConfiguration,
  moduleABTestingConfiguration,
  pageType,
  id,
  title,
}: AnalyticsTrackerProps) {
  const isClarityReady = useClarityReady()

  const pageVariant = useMemo(() => {
    if (!abTestingConfiguration?.active || !abTestingConfiguration?.pageVariants?.length) {
      return 'default'
    }

    // 1. Try to use selectedVariantName from server (most efficient)
    const preferredName = (abTestingConfiguration as unknown as { selectedVariantName?: string })?.selectedVariantName
    if (preferredName) {
      return preferredName
    }

    const cookieName = abTestingConfiguration.cookieName || ''
    const pageTemplateId = abTestingConfiguration.pageTemplateId || ''

    // 2. Try to use selectedVariantId from server (template ID)
    const preferredId = (abTestingConfiguration as unknown as { selectedVariantId?: string })?.selectedVariantId
    if (preferredId) {
      const variant = abTestingConfiguration.pageVariants.find((v) =>
        v.controlVariant ? preferredId === pageTemplateId : v.variantId === preferredId
      )
      if (variant) {
        return variant.variantName
      }
    }

    // 3. Fall back to cookie value (template ID)
    const variantId = getVariantIdCookie(cookieName)
    if (variantId) {
      const variant = abTestingConfiguration.pageVariants.find((v) =>
        v.controlVariant ? variantId === pageTemplateId : v.variantId === variantId
      )
      if (variant) {
        return variant.variantName
      }
    }

    return 'default'
  }, [abTestingConfiguration])

  useEffect(() => {
    if (!analyticsService) return

    const trackingData: any = {
      page_variant: pageVariant,
      content_type: pageType,
      content_id: id,
      page_name: title,
      page_url_clean: window.location.origin + window.location.pathname,
      business_region: extractRegionFromUrl(window.location.href) as string,
      site_name: window.location.hostname,
    }

    // Add module variants if present
    if (moduleABTestingConfiguration?.selectedNavigationVariant) {
      trackingData.navigation_variant = moduleABTestingConfiguration.selectedNavigationVariant
    }
    if (moduleABTestingConfiguration?.selectedFooterVariant) {
      trackingData.footer_variant = moduleABTestingConfiguration.selectedFooterVariant
    }

    analyticsService.trackPage(trackingData)
  }, [id, pageType, title, pageVariant, moduleABTestingConfiguration])

  useEffect(() => {
    if (isClarityReady) {
      setPageVariantInClarity(pageVariant)
      if (moduleABTestingConfiguration?.selectedNavigationVariant) {
        setNavigationVariantInClarity(moduleABTestingConfiguration.selectedNavigationVariant)
      }
      if (moduleABTestingConfiguration?.selectedFooterVariant) {
        setFooterVariantInClarity(moduleABTestingConfiguration.selectedFooterVariant)
      }
    }
  }, [isClarityReady, pageVariant, moduleABTestingConfiguration])

  return null
}
